/**
 * app.module.ts
 *
 * @version
 * 4.68.0 DEVOPS-928 Add environment route resolver to all Angular repos
 * 4.64.0 OAUI-646 Upgrades for Angular v16
 * 4.63.0 DEVOPS-715 order-admin-ui: setup new repo without vertical slicing
 * 4.56.0 OAUI-614 Migrate LMV navigation bar into angular
 * 4.34.0 OAUI-491 Update order dates
 * 4.31.0 OAUI-453 Set up basic list settings, filters and layout
 * 4.26.0 OAUI-416 Add basic QR code order management route
 * 4.23.0 OAUI-408 Paginating in Work Orders is slow
 *
 * @author Luis Hernandez <lhernandez@intersog.com>
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 *
 * @copyright (c) 2021-2023 Build Centrix
 */
import { ApplicationRef, DoBootstrap, Injector, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UpgradeModule } from '@angular/upgrade/static';
import { BcComponentsModule } from 'ng-library-style-guide/dist/bc-style-guide';
import { SharedModule } from 'src/app/components/shared.module';
import { AuthGuard } from './guards/auth.guard';
import { ErrorsModule } from './pages/errors/components/errors.module';
import { OrdersModule } from './pages/orders/orders.module';
import { BlankApp } from './angularjs/angularjsapp';
import wdEnvironmentService from 'wd-library-environment';
import {
  helpers as helpersLibraryService,
  services as mainLibraryService,
} from 'main-library-js';
// import ngx-translate and the http loader
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { BcxTranslateHttpLoader } from '../translations/bcx-translate-http-loader';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { MatDialog } from '@angular/material/dialog';
import { setUpLocationSync } from '@angular/router/upgrade';
import { WdLocalEnvironmentService } from './services/environment/wd-local-environment.service';
import { CommonModule } from '@angular/common';
import 'angular-moment';
import { ModelViewerModule } from './pages/modelViewer/model-viewer.module';

@NgModule({
  declarations: [AppComponent],
  imports: [
    AppRoutingModule,
    HttpClientModule,
    BcComponentsModule,
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    ErrorsModule,
    OrdersModule,
    ModelViewerModule,
    UpgradeModule,
    FormsModule,
    SharedModule,
    // ngx-translate and the loader module
    HttpClientModule,
    TranslateModule.forRoot({
      defaultLanguage: 'EN',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    AuthGuard,
    MatDialog,
    { provide: '$scope', useExisting: '$rootScope' },
    {
      provide: 'wdEnvironmentService',
      useFactory: (i: any) => i.get('wdEnvironmentService'),
      deps: ['$injector'],
    },
    {
      provide: 'wdOauth2HttpService',
      useFactory: (i: any) => i.get('wdOauth2HttpService'),
      deps: ['$injector'],
    },
    ...Object.keys(mainLibraryService).map((key) => {
      return {
        provide: key,
        useFactory: (i: any) => i.get(key),
        deps: ['$injector'],
      };
    }),
    ...Object.keys(helpersLibraryService).map((key) => {
      return {
        provide: key,
        useFactory: (i: any) => i.get(key),
        deps: ['$injector'],
      };
    }),
  ],
})
export class AppModule implements DoBootstrap {
  constructor(private upgrade: UpgradeModule, public injector: Injector) {}

  public async ngDoBootstrap(ref: ApplicationRef) {
    this.upgrade.bootstrap(document.body, [
      BlankApp.name,
      wdEnvironmentService.name,
      ...Object.values(mainLibraryService),
      ...Object.values(helpersLibraryService),
    ]);

    await this.initialize();
    ref.bootstrap(AppComponent);
    setUpLocationSync(this.upgrade); // https://github.com/angular/angular/issues/14081
  }

  /**
   * Initialize wd-environment-service
   *
   * @since 4.63.0
   *
   * @memberof AppModule
   */
  private async initialize(): Promise<void> {
    await this.injector.get(WdLocalEnvironmentService).initialize();
  }
}
/**
 * Required for AOT translations compilation
 * @since 4.26.0
 */
export function HttpLoaderFactory(http: HttpClient): BcxTranslateHttpLoader {
  return new BcxTranslateHttpLoader('EN', http, './assets/i18n/', '.json');
}
